import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Trash2, Paperclip, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { updateReport, deleteReport, reportAttachmentUrl } from "@/lib/api";
import { withAdminPin } from "@/lib/adminPin";

const STATUS_META = {
  open:        { label: "Open",         badge: "border-blue-500/40 bg-blue-500/10 text-blue-300" },
  in_progress: { label: "In Progress",  badge: "border-yellow-500/40 bg-yellow-500/10 text-yellow-300" },
  resolved:    { label: "Resolved",     badge: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${meta.badge}`}
    >
      {meta.label}
    </span>
  );
}

function StatusDropdown({ current, onChange, disabled }) {
  return (
    <select
      data-testid="report-status-select"
      value={current}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="bg-[#FFFFFF] border border-slate-200 mono text-[11px] uppercase tracking-widest px-2 py-1 text-white focus:outline-none focus:border-[#2563EB] rounded-none"
    >
      <option value="open">Open</option>
      <option value="in_progress">In Progress</option>
      <option value="resolved">Resolved</option>
    </select>
  );
}

export default function ReportItem({ report, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(report.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const created = new Date(report.created_at);
  const updated = new Date(report.updated_at);

  const changeStatus = async (next) => {
    if (next === report.status) return;
    setSaving(true);
    try {
      const result = await withAdminPin((pin) => updateReport(report.id, { status: next }, pin));
      if (result) {
        toast.success(`Status → ${STATUS_META[next].label}`);
        onChanged?.();
      }
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const result = await withAdminPin((pin) => updateReport(report.id, { admin_notes: notes }, pin));
      if (result) {
        toast.success("Notes saved");
        onChanged?.();
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete report "${report.title}"?`)) return;
    try {
      const result = await withAdminPin((pin) => deleteReport(report.id, pin));
      if (result) {
        toast.success("Report deleted");
        onChanged?.();
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      className="bg-[#F8FAFC] border border-slate-200 fade-up"
      data-testid={`report-item-${report.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-[#64748B]" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[#64748B]" />
            )}
            <StatusBadge status={report.status} />
            <span className="heading font-semibold truncate">{report.title}</span>
            {report.has_attachment && (
              <Paperclip className="w-3.5 h-3.5 text-[#2563EB]" />
            )}
          </div>
          <div className="mono text-[11px] text-[#64748B] mt-1 pl-6">
            {created.toLocaleString()}
            {report.contact ? ` · ${report.contact}` : ""}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200 space-y-4 pt-4">
          {report.description && (
            <div>
              <div className="overline mb-1">Description</div>
              <div className="mono text-xs text-white/90 whitespace-pre-wrap">
                {report.description}
              </div>
            </div>
          )}

          {report.has_attachment && (
            <div>
              <div className="overline mb-1">Attachment</div>
              <a
                data-testid={`report-attachment-${report.id}`}
                href={reportAttachmentUrl(report.id)}
                target="_blank"
                rel="noreferrer"
                className="mono text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1.5"
              >
                <Paperclip className="w-3.5 h-3.5" />
                {report.attachment_filename}
                <span className="text-[#64748B]">
                  ({(report.attachment_size / 1024).toFixed(0)} KB)
                </span>
              </a>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <div>
              <div className="overline mb-1">Status</div>
              <StatusDropdown
                current={report.status}
                onChange={changeStatus}
                disabled={saving}
              />
            </div>
            <div className="text-xs text-[#64748B] mono">
              Updated {updated.toLocaleString()}
            </div>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              data-testid={`delete-report-${report.id}`}
              onClick={remove}
              className="text-slate-500 hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-md mono uppercase text-[10px] tracking-widest"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </div>

          <div>
            <div className="overline mb-1">Admin notes</div>
            <Textarea
              data-testid={`admin-notes-${report.id}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What did we find? What's the fix? Any workarounds…"
              className="bg-[#FFFFFF] border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-xs"
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                data-testid={`save-notes-${report.id}`}
                disabled={saving || notes === (report.admin_notes || "")}
                onClick={saveNotes}
                className="bg-slate-100 hover:bg-[#2563EB] hover:text-white text-slate-700 rounded-md mono uppercase text-[10px] tracking-widest"
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Save notes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
